import { Loader2 } from 'lucide-react'
import React, { useEffect, useState } from 'react'

import { Badge } from 'slab'

import { DrawerProvider, useDrawer } from './Drawer'
import Notes, { type Note } from './Notes'
import { useCreateNote } from '../hooks/useCreateNote'
import { getNotesQueryKey, useGetNotes } from '../hooks/useGetNotes'

interface NotesStatusComponentProps {
  isLoading: boolean
  notesCount: number
}

const NotesStatusComponent = ({ isLoading, notesCount }: NotesStatusComponentProps) => {
  if (isLoading) {
    return <Loader2 size={16} className='animate-spin text-gray-500' />
  }

  return (
    <div className='flex items-center justify-center text-xs font-medium text-gray-600'>
      <Badge type='info'>{notesCount}</Badge>
    </div>
  )
}

// Component that handles auto-expansion (must be inside DrawerProvider)
const MemberNotesAutoExpander = ({ notes }: { notes?: Note[] }) => {
  const { setIsExpanded } = useDrawer()

  // Expand the drawer if there are 3 or more notes.
  useEffect(() => {
    if (notes && notes.length >= 3) {
      setIsExpanded(true)
    }
  }, [notes, setIsExpanded])

  return null // This component only handles side effects
}

const NotesHeader = () => (
  <h2 className='h2 m-0'>Notes</h2>
)

interface MemberNotesProps {
  displayExistingNotes?: boolean
  memberId: string
  subjectId?: string
  subjectType?: string
}

const MemberNotes = ({
  displayExistingNotes = true,
  memberId,
  subjectId,
  subjectType,
}: MemberNotesProps) => {
  // NOTE: DO NOT include employerId in the fetch query, as this will include internal notes meant only for Thatch Admins.
  // The endpoint will only let you fetch one of memberId or employerId, not both.
  const {
    data: notes,
    error,
    isLoading,
  } = useGetNotes({ memberId, displayExistingNotes })
  const createNoteMutation = useCreateNote({ memberId, subjectType, subjectId })
  const notesQueryKey = getNotesQueryKey({ memberId })

  // Both of these may only become true (they don't get set back to false)
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false)
  const [isUnauthorized, setIsUnauthorized] = useState(false)

  useEffect(() => {
    const receivedData = Boolean(notes || error)
    if (receivedData) {
      setIsInitialLoadComplete(true)
    }
  }, [error, notes, setIsInitialLoadComplete])

  useEffect(() => {
    if (error?.status === 401) {
      setIsUnauthorized(true)
    }
  }, [error, setIsUnauthorized])

  const notesCount = notes?.length || 0

  // Don't display if initial data load hasn't completed or if the user's
  // not authorized to view the data
  if (!isInitialLoadComplete || isUnauthorized) {
    return null
  }

  return (
    <DrawerProvider
      statusComponent={
        <NotesStatusComponent isLoading={isLoading} notesCount={notesCount} />
      }
      headerComponent={<NotesHeader />}
    >
      {/* Auto-expander inside DrawerProvider so it can access drawer context */}
      <MemberNotesAutoExpander notes={notes} />
      <div className='h-full flex flex-col overflow-hidden' data-drawer='notes'>
        {/* Scrollable content area */}
        <div className='flex-1 overflow-y-auto overflow-x-hidden p-4 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent hover:scrollbar-thumb-gray-400'>
          <Notes
            createNoteMutation={createNoteMutation}
            displayExistingNotes={displayExistingNotes}
            error={error}
            isLoading={isLoading}
            notes={notes || []}
            queryKey={notesQueryKey}
          />
        </div>
      </div>
    </DrawerProvider>
  )
}

export default MemberNotes
